// Integrazione Umami (self-hosted) lato frontend EmmaMaison-web.
// Analytics privacy-first, cookieless. Nessuna dipendenza npm: si inietta lo
// script ufficiale del tracker in <head> a runtime.
//
// Principi (allineati a src/utilities/sentry.js):
// - Nessuna funzione qui deve MAI lanciare o bloccare l'app.
// - Se le env non sono configurate -> no-op totale.
// - Nessun invio di PII/segreti: i dati evento sono sanitizzati difensivamente
//   (chiavi sensibili filtrate, query string/hash rimossi, profondità/size limitate).
//
// Config via env (build-time, prefisso VUE_APP_*; NIENTE valori hardcoded):
//   VUE_APP_UMAMI_ENABLED       "true" per attivare; qualsiasi altro valore = no-op
//   VUE_APP_UMAMI_SCRIPT_URL    URL dello script.js del tracker (obbligatorio)
//   VUE_APP_UMAMI_WEBSITE_ID    website id generato da Umami (obbligatorio)
//   VUE_APP_UMAMI_HOST_URL      opz. host del collector (data-host-url)
//   VUE_APP_UMAMI_DOMAINS       opz. domini consentiti, CSV (data-domains)
//   VUE_APP_UMAMI_TAG           opz. tag di campagna/ambiente (data-tag)
//
// Docs: https://umami.is/docs/tracker-configuration | .../tracker-functions
//       .../track-single-page-apps (auto-track SPA attivo di default).

// --- Sanitizzazione (difensiva, mai lancia) ------------------------------

// Chiavi il cui valore non deve mai finire in analytics.
const SENSITIVE_KEYS = [
  "token",
  "password",
  "secret",
  "authorization",
  "cookie",
  "email",
  "phone",
  "telefono",
  "credential",
  "session",
];

function isSensitiveKey(key) {
  const k = String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  return SENSITIVE_KEYS.some((s) => k.includes(s));
}

// Rimuove query string / fragment, mantenendo il path. Su stringhe che
// sembrano URL o path ("/x", "http(s)://x") taglia da "?" o "#" in poi.
function stripQueryAndHash(value) {
  if (typeof value !== "string") return value;
  const looksLikeUrl = value.startsWith("/") || /^https?:\/\//i.test(value);
  if (!looksLikeUrl) return value;
  const i = value.search(/[?#]/);
  return i === -1 ? value : value.slice(0, i);
}

// Limiti anti-abuso per i payload evento.
const MAX_DEPTH = 3;
const MAX_KEYS = 30;
const MAX_STRING = 500;

// Sanifica un valore evento in modo ricorsivo e guardato: filtra chiavi
// sensibili, rimuove query/hash dagli URL/path, limita profondità e size.
// Restituisce sempre un nuovo valore (non muta l'input). Non lancia mai.
function sanitizeValue(value, depth = 0) {
  try {
    if (value == null) return value;

    if (typeof value === "string") {
      const out = stripQueryAndHash(value);
      return out.length > MAX_STRING ? out.slice(0, MAX_STRING) : out;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return value;
    }

    if (depth >= MAX_DEPTH) return undefined;

    if (Array.isArray(value)) {
      const out = [];
      for (const item of value.slice(0, MAX_KEYS)) {
        const s = sanitizeValue(item, depth + 1);
        if (s !== undefined) out.push(s);
      }
      return out;
    }

    if (typeof value === "object") {
      const out = {};
      let count = 0;
      for (const [k, v] of Object.entries(value)) {
        if (count >= MAX_KEYS) break;
        if (isSensitiveKey(k)) continue; // scarta interamente le chiavi sensibili
        const s = sanitizeValue(v, depth + 1);
        if (s !== undefined) {
          out[k] = s;
          count += 1;
        }
      }
      return out;
    }

    // funzioni, symbol, ecc.: non inviare.
    return undefined;
  } catch {
    return undefined;
  }
}

function sanitizeEventData(data) {
  if (data == null || typeof data !== "object") return undefined;
  const out = sanitizeValue(data, 0);
  return out && typeof out === "object" && Object.keys(out).length
    ? out
    : undefined;
}

// --- Init / iniezione script --------------------------------------------

// Marker per garantire iniezione una sola volta anche a fronte di chiamate
// multiple (HMR in dev, doppio import, ecc.).
const SCRIPT_MARKER = "data-emmamaison-umami";
let injected = false;

function readConfig() {
  const enabled =
    String(process.env.VUE_APP_UMAMI_ENABLED || "").toLowerCase() === "true";
  return {
    enabled,
    scriptUrl: process.env.VUE_APP_UMAMI_SCRIPT_URL || "",
    websiteId: process.env.VUE_APP_UMAMI_WEBSITE_ID || "",
    hostUrl: process.env.VUE_APP_UMAMI_HOST_URL || "",
    domains: process.env.VUE_APP_UMAMI_DOMAINS || "",
    tag: process.env.VUE_APP_UMAMI_TAG || "",
  };
}

// Website id configurato (o "" se assente). Esposto perché il ticket di
// assistenza lo allega come campo tecnico per correlare ticket ↔ analytics.
export function getUmamiWebsiteId() {
  try {
    return process.env.VUE_APP_UMAMI_WEBSITE_ID || "";
  } catch {
    return "";
  }
}

// Inizializza Umami iniettando lo script del tracker in <head>. No-op se non
// configurato (o già iniettato, o fuori dal browser). Non lancia mai.
// L'auto-track SPA di Umami gestisce le pageview delle navigazioni client-side:
// nessun tracking manuale via router per evitare doppioni.
export function initUmami() {
  try {
    if (injected) return;
    if (typeof document === "undefined" || !document.head) return;

    const cfg = readConfig();
    // No-op silenzioso se disattivato o mancano i parametri obbligatori.
    if (!cfg.enabled || !cfg.scriptUrl || !cfg.websiteId) return;

    // Guardia extra: se un tag equivalente esiste già, non re-iniettare.
    if (document.querySelector(`script[${SCRIPT_MARKER}]`)) {
      injected = true;
      return;
    }

    const el = document.createElement("script");
    el.defer = true;
    el.src = cfg.scriptUrl;
    el.setAttribute(SCRIPT_MARKER, "1");
    el.setAttribute("data-website-id", cfg.websiteId);
    if (cfg.hostUrl) el.setAttribute("data-host-url", cfg.hostUrl);
    if (cfg.domains) el.setAttribute("data-domains", cfg.domains);
    if (cfg.tag) el.setAttribute("data-tag", cfg.tag);
    // Privacy/qualità dati: non contare le query di ricerca come pagine
    // distinte e rispetta l'header Do Not Track del browser.
    el.setAttribute("data-exclude-search", "true");
    el.setAttribute("data-do-not-track", "true");

    document.head.appendChild(el);
    injected = true;
  } catch {
    // Mai bloccare l'app per un problema di analytics.
  }
}

// --- Tracker functions (safe) -------------------------------------------
//
// Test manuale dalla console DevTools: queste funzioni sono ES module e NON
// sono importabili dalla console. Per un MVP è sufficiente validare che
// script.js si carichi, che parta la POST /api/send navigando, e vedere la
// visita nella Live view di Umami. Se serve provare un evento a mano, una volta
// caricato lo script si può chiamare direttamente il tracker globale:
//   window.umami && window.umami.track("test_evento", { origine: "console" })
// (bypassa il sanitizer di questo modulo: usarlo solo per verifica tecnica).

// window.umami è disponibile solo dopo il caricamento (defer) dello script.
function getTracker() {
  try {
    return typeof window !== "undefined" ? window.umami : undefined;
  } catch {
    return undefined;
  }
}

// Traccia un evento custom. No-op se Umami non è pronto. I dati sono
// sanitizzati (chiavi sensibili filtrate, query/hash rimossi, size limitata).
// Es: trackUmamiEvent("quote_created", { category: "piastrelle" }).
export function trackUmamiEvent(name, data) {
  try {
    const umami = getTracker();
    if (!umami || typeof umami.track !== "function") return;
    if (!name || typeof name !== "string") return;

    const safe = sanitizeEventData(data);
    if (safe) umami.track(name, safe);
    else umami.track(name);
  } catch {
    // no-op
  }
}

// Associa proprietà/id a una sessione (Umami identify). No-op se non pronto.
// I dati sono sanitizzati: eventuali email/telefono/token vengono scartati.
//
// Due forme supportate (la seconda è retrocompatibile con la firma originale):
//   identifyUmamiUser(id, data?)  → umami.identify(id, data)
//   identifyUmamiUser(data)       → umami.identify(data)
// L'id deve essere un identificatore ANONIMO (vedi utilities/correlationId.js):
// non passare mai user id applicativi, email o nomi.
function doIdentify(idOrData, data) {
  const umami = getTracker();
  if (!umami || typeof umami.identify !== "function") return false;

  if (typeof idOrData === "string") {
    if (!idOrData) return true; // niente id: nulla da fare, ma non ritentare
    const safe = sanitizeEventData(data);
    if (safe) umami.identify(idOrData, safe);
    else umami.identify(idOrData);
    return true;
  }

  const safe = sanitizeEventData(idOrData);
  if (safe) umami.identify(safe);
  return true;
}

// Lo script è caricato in `defer`: al momento del bootstrap window.umami può non
// esistere ancora. Ritentiamo poche volte a intervallo breve (best-effort), poi
// ci arrendiamo silenziosamente: l'identify non è mai critico.
const IDENTIFY_RETRIES = 10;
const IDENTIFY_RETRY_MS = 300;

export function identifyUmamiUser(idOrData, data) {
  let attempts = 0;
  const attempt = () => {
    try {
      if (doIdentify(idOrData, data)) return;
    } catch {
      return; // errore reale: non insistere
    }
    if (++attempts >= IDENTIFY_RETRIES) return;
    try {
      setTimeout(attempt, IDENTIFY_RETRY_MS);
    } catch {
      /* no-op */
    }
  };
  attempt();
}
